<template>
  <section>
    <div class="ca-bullet-container">
      <div class="ca-noticias-wrapper">
        <nav aria-label="Noticias" class="ca-noticias flex flex-col gap-size-5">
          <div class="ca-noticias__header">
            <CaModuleMessage
              :label="model.label"
              :title="model.title"
              :title-heading-type="model.titleHeadingType || 'H2'"
            />
            <div
              v-if="cards.length > 2"
              class="ca-noticias__arrows"
              :class="{
                'ca-noticias__arrows--gt2': cards.length > 2,
                'ca-noticias__arrows--gt3': cards.length > 3,
              }"
            >
              <IconButton
                class="ca-noticias__arrow"
                :class="{ 'ca-noticias__arrow--disabled': !canScrollPrev }"
                type="theme"
                variant="tertiary"
                size="s"
                :disabled="!canScrollPrev"
                aria-label="Conteúdo anterior"
                @clicked="scrollPrev"
              >
                <template #trailingIcon>
                  <ChevronLeft />
                </template>
              </IconButton>
              <IconButton
                class="ca-noticias__arrow"
                :class="{ 'ca-noticias__arrow--disabled': !canScrollNext }"
                type="theme"
                variant="tertiary"
                :disabled="!canScrollNext"
                aria-label="Conteúdo seguinte"
                @clicked="scrollNext"
              >
                <template #trailingIcon>
                  <ChevronRight />
                </template>
              </IconButton>
            </div>
          </div>
          <div v-if="cards.length" class="ca-noticias__viewport">
            <div
              ref="sliderRef"
              class="ca-noticias__slider"
              role="region"
              aria-roledescription="carousel"
              aria-label="Notícias"
            >
              <div class="ca-noticias__track">
                <CaQuickActionCard
                  v-for="(card, index) in cards"
                  :key="index"
                  class="ca-noticias__card"
                  role="group"
                  aria-roledescription="slide"
                  :aria-label="`Cartão ${index + 1} de ${cards.length}. ${card.title}`"
                  :title="card.title"
                  :title-heading-type="card.titleHeadingType"
                  :description="card.description"
                  :description-heading-type="card.descriptionHeadingType"
                  :backgroundColor="normalizeColor(card.color)"
                  :size="normalizedSize"
                  :image="card.iconUrl"
                  :avatarBackgroundColorClass="card.iconBackgroundColor"
                  :url="card.url"
                  :target="card.target"
                />
              </div>
            </div>
          </div>

          <!-- Page dots (mobile only) -->
          <CaPageControl
            v-if="cards.length > 1"
            class="ca-noticias__dots"
            :count="cards.length"
            :model-value="activeSlide"
            @update:model-value="scrollToSlide"
          />

          <LinkButton
            :label="model.buttonLabel"
            :aria-label="model.buttonLabel"
            type="default"
            class="self-center"
            icon="arrow_right"
            @click="handleButtonClick"
          >
          </LinkButton>
        </nav>
      </div>
    </div>
  </section>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import CaQuickActionCard from "../../base/cards/CaQuickActionCard.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import CaPageControl from "../../base/CaPageControl.vue";
import { LinkButton, IconComponent, IconButton } from "ca-online-design-system";
import ChevronLeft from "@assets/chevron-left-green.svg";
import ChevronRight from "@assets/chevron-right-green.svg";

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

// Para import dinâmico em dev, evitando incluir o mock no bundle de produção
const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const cards = computed(() => model.value.cards ?? []);

const normalizedSize = computed(() => {
  const size = String(model.value.size || "S").toUpperCase();
  return ["S", "M", "L"].includes(size) ? size : "S";
});

const backgroundVariants = {
  laranja: "laranja",
  verde: "verde",
  branco: "branco",
};

/** Normalize a model color (e.g. "verde") to the base card variant. */
function normalizeColor(color) {
  return backgroundVariants[String(color || "").toLowerCase()] ?? "branco";
}

// ---------------------------------------------------------------------------
// Slider (carousel) state & navigation.
// One page-control dot / arrow step per card; the number of cards visible at
// once is controlled purely in CSS (1 mobile, 2 tablet, 3 desktop).
// ---------------------------------------------------------------------------
const sliderRef = ref(null);
const activeSlide = ref(0);
const canScrollPrev = ref(false);
const canScrollNext = ref(false);
let sliderRo = null;

function getCards() {
  return Array.from(
    sliderRef.value?.querySelectorAll(".ca-noticias__card") || [],
  );
}

/**
 * Distance (px) between two consecutive card starts = card width + gap.
 * Measured from the real layout (não sofre clamp), serve de "passo" de scroll.
 */
function getStep() {
  const items = getCards();
  if (items.length < 2) return 0;
  return items[1].offsetLeft - items[0].offsetLeft;
}

function updateScrollState() {
  const el = sliderRef.value;
  if (!el) return;
  const epsilon = 2;
  canScrollPrev.value = el.scrollLeft > epsilon;
  canScrollNext.value =
    el.scrollLeft + el.clientWidth < el.scrollWidth - epsilon;
  // Índice ativo a partir do scroll real (sem clamp): evita ficar "preso" nos
  // últimos cartões, que partilham o mesmo scroll máximo.
  const step = getStep();
  activeSlide.value = step ? Math.round(el.scrollLeft / step) : 0;
}

/** Navega um cartão para trás/frente por deslocamento relativo. */
function scrollByCards(direction) {
  const el = sliderRef.value;
  const step = getStep();
  if (!el || !step) return;
  el.scrollBy({ left: direction * step, behavior: "smooth" });
}

function scrollPrev() {
  scrollByCards(-1);
}

function scrollNext() {
  scrollByCards(1);
}

function scrollToSlide(idx) {
  const el = sliderRef.value;
  const step = getStep();
  if (!el || !step) return;
  el.scrollTo({ left: idx * step, behavior: "smooth" });
}

/** Container background variants (includes a neutral/grey option). */
const containerBackgroundVariants = {
  white: "white",
  solarized: "solarized",
};

/** Background of the container itself, coming from the model. */
const containerBackgroundClass = computed(() => {
  const variant = String(model.value.backgroundColor || "").toLowerCase();
  const mapped = containerBackgroundVariants[variant];
  return mapped ? `ca-quick-action-cards--${mapped}` : "";
});

/** Rounded corners variant (top/bottom) coming from the model. */
const roundedCornersClass = computed(() => {
  const variant = String(model.value.roundedCorners || "").toLowerCase();
  return ["top", "bottom"].includes(variant)
    ? `ca-quick-action-cards--rounded-${variant}`
    : "";
});

/** Navigate to the URL provided in model.buttonUrl. */
function handleButtonClick() {
  const url = model.value.buttonUrl;
  if (!url) return;
  window.location.href = url;
}

onMounted(async () => {
  // Em desenvolvimento, se não receber um model via props, carrega o mock para facilitar testes
  if (isDev && !modelRef.value) {
    const { mockActionCards4 } = await import("./mocks/noticiasMocks.js");
    modelRef.value = mockActionCards4;
  }

  await nextTick();
  const el = sliderRef.value;
  if (el) {
    el.addEventListener("scroll", updateScrollState, { passive: true });
    sliderRo = new ResizeObserver(updateScrollState);
    sliderRo.observe(el);
    updateScrollState();
  }
});

onBeforeUnmount(() => {
  const el = sliderRef.value;
  if (el) el.removeEventListener("scroll", updateScrollState);
  if (sliderRo) {
    sliderRo.disconnect();
    sliderRo = null;
  }
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Wrapper vertical padding (mobile first): size-8 (mobile); tablet/desktop
// override para size-11. A classe duplicada eleva a especificidade para bater
// utilitárias `.ds-scope` com a mesma classe.
// ---------------------------------------------------------------------------
.ca-noticias-wrapper {
  padding-block: var(--size-8);
}

@media (min-width: 768px) {
  .ca-noticias-wrapper {
    padding-block: var(--size-11);
  }
}

.ca-noticias {
  // Padding lateral (p-size-4) deixa de estar no nav: é aplicado por componente
  // (header/dots) via --ca-pad; o slider fica full-bleed e o card desconta-o na
  // largura. Apenas o padding vertical fica no próprio nav.
  --ca-pad: var(--size-4);
  background-color: var(--ca-color-backgrounds-container);
  // Border radius mobile-first: 24px (mobile), 32px (tablet), 48px (desktop).
  border-radius: 24px;
  padding-block: var(--ca-pad);
}
// ---------------------------------------------------------------------------
// Section padding (mobile first). Base vertical padding (size-9) is applied via
// DS utility classes in the template. The DS stylesheet does not emit md:/lg:
// padding variants, so tablet/desktop overrides use media queries with DS
// spacing tokens (var(--size-*)).
// ---------------------------------------------------------------------------
@media (min-width: 768px) {
  // Tablet: pad size-5, gap maior (size-7) e radius 32px. A classe
  // duplicada eleva a especificidade para vencer as utilitárias
  // `.ds-scope .gap-size-5`.
  .ca-noticias.ca-noticias {
    --ca-pad: var(--size-5);
    gap: var(--size-7);
    border-radius: 32px;
  }
}

@media (min-width: 1024px) {
  // Desktop: pad size-6, mantém gap size-7 (herdado do tablet) e radius 48px.
  .ca-noticias.ca-noticias {
    --ca-pad: var(--size-6);
    border-radius: 48px;
    gap: var(--size-7);
  }
}

// ---------------------------------------------------------------------------
// Slider (carousel) — mobile first.
// Cartões visíveis por breakpoint: 1 (mobile), 2 (tablet >= 768px),
// 3 (desktop >= 1024px). Scroll horizontal com scroll-snap; navegação por
// page dots (mobile) e setas (tablet/desktop).
// ---------------------------------------------------------------------------
.ca-noticias__slider {
  --slider-gap: var(--size-5);
  --slider-visible: 1;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-snap-type: x mandatory;
  overscroll-behavior-inline: contain;
  // Snap deixa --ca-pad de recuo à esquerda, alinhando o card com o header.
  scroll-padding-inline: var(--ca-pad);

  &::-webkit-scrollbar {
    display: none;
  }
}

.ca-noticias__track {
  display: flex;
  gap: var(--slider-gap);
  align-items: stretch;

  // Spacer real à direita (o margin-inline-end do último item é ignorado no
  // overflow de scroll): dá espaço para o último card parar recuado. O margin
  // negativo cancela o gap que o flex adiciona antes do spacer.
  &::after {
    content: "";
    flex: 0 0 var(--ca-pad);
    margin-inline-start: calc(-1 * var(--slider-gap));
  }
}

.ca-noticias__card {
  flex-shrink: 0;
  // Largura para caberem exatamente `--slider-visible` cartões dentro da área
  // útil (largura menos o recuo --ca-pad dos dois lados), descontando os gaps.
  width: calc(
    (
        100% - 2 * var(--ca-pad) - (var(--slider-visible) - 1) *
          var(--slider-gap)
      ) /
      var(--slider-visible)
  );
  scroll-snap-align: start;
  scroll-snap-stop: always;

  // Recuo à esquerda do 1º card (alinha com o header); as pontas são tapadas
  // pelas cortinas do viewport.
  &:first-child {
    margin-inline-start: var(--ca-pad);
  }
}

@media (min-width: 768px) {
  .ca-noticias__slider {
    --slider-visible: 2;
  }
}

@media (min-width: 1024px) {
  .ca-noticias__slider {
    --slider-visible: 3;
  }
}

// ---------------------------------------------------------------------------
// Header: module message + setas de navegação (tablet/desktop) no topo direito.
// ---------------------------------------------------------------------------
.ca-noticias__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--size-4);
  padding-inline: var(--ca-pad);
}

// ---------------------------------------------------------------------------
// Controls: page dots (mobile) + setas (tablet/desktop)
// ---------------------------------------------------------------------------
// Page dots: visíveis só em mobile. Re-tingidos para o fundo claro do container
// (o CaPageControl usa por omissão dots brancos, pensados para fundos escuros).
.ca-noticias__dots {
  padding-inline: var(--ca-pad);

  :deep(.ca-page-control__dot) {
    background-color: var(--ca-color-neutrals-on-background-low-30);
  }

  :deep(.ca-page-control__dot--active) {
    background-color: var(--ca-color-primary, #066542);
  }
}

// Setas: escondidas em mobile. Visíveis apenas quando há mais cartões do que
// os visíveis no breakpoint: tablet (2 visíveis) => > 2 cartões; desktop
// (3 visíveis) => > 3 cartões.
.ca-noticias__arrows {
  display: none;
  gap: var(--size-4);
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .ca-noticias__dots {
    display: none !important;
  }

  // Tablet: setas quando há mais de 2 cartões.
  .ca-noticias__arrows--gt2 {
    display: flex;
  }
}

@media (min-width: 1024px) {
  // Desktop: esconde o caso "exatamente 3" e só mostra com mais de 3 cartões.
  .ca-noticias__arrows {
    display: none;
  }

  .ca-noticias__arrows--gt3 {
    display: flex;
  }
}

// Botões de navegação (setas) — IconButton do DS com override para o visual
// da imagem: círculos brancos com chevron verde.
.ca-noticias__arrow {
  width: 48px !important;
  height: 48px !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 100px !important;
  color: var(--ca-color-theme-primary, #066542);
  background-color: var(--ca-color-neutrals-inverted, #fff) !important;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.08);
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  // O chevron (SVG com fill fixo) passa a seguir o `color` do botão, para os
  // estados enabled (verde) e disabled (cinzento) serem controlados por `color`.
  :deep(svg path) {
    fill: currentColor !important;
  }

  &:hover {
    background-color: var(
      --ca-color-empresas-solarized-medium,
      #edecea
    ) !important;
  }

  &--disabled {
    opacity: 0.5;
  }
}

// ---------------------------------------------------------------------------
// Container background variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--white {
  background: var(--ca-color-neutrals-inverted);
}

.ca-quick-action-cards--solarized {
  background: var(--ca-color-backgrounds-container);
}

// ---------------------------------------------------------------------------
// Rounded corners variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--rounded-top {
  border-top-left-radius: var(--size-5);
  border-top-right-radius: var(--size-5);
}

.ca-quick-action-cards--rounded-bottom {
  border-bottom-left-radius: var(--size-5);
  border-bottom-right-radius: var(--size-5);
}

@media (min-width: 1024px) {
  .ca-quick-action-cards--rounded-top {
    border-top-left-radius: var(--size-8);
    border-top-right-radius: var(--size-8);
  }

  .ca-quick-action-cards--rounded-bottom {
    border-bottom-left-radius: var(--size-8);
    border-bottom-right-radius: var(--size-8);
  }
}
</style>

<style lang="scss">
.ca-noticias__arrow--disabled {
  pointer-events: none;
}
</style>
